import React from "react";
import { X } from "@phosphor-icons/react";
import { formatINR } from "../Dashboard/Promotions/promoUtils";

const STATUS_LABELS = {
    Pending_Hotel: "Pending on you",
    Pending_Guest: "Pending on guest",
    InProgress_Hotel: "Ongoing",
    Scheduled_Hotel: "Scheduled",
    Completed_Hotel: "Completed",
    Completed_Guest: "Completed",
    Closed_Hotel: "Closed",
    Closed_Guest: "Cancelled by guest",
    Declined_Hotel: "Declined by you",
    Declined_Guest: "Declined by guest",
};

const STATUS_STYLES = {
    Pending_Hotel: "bg-[#FF5A5F]",
    Pending_Guest: "bg-[#A88737]",
    InProgress_Hotel: "bg-[#0B22B2]",
    Scheduled_Hotel: "bg-[#6DABA1]",
    Completed_Hotel: "bg-[#2FA56A]",
    Completed_Guest: "bg-[#2FA56A]",
    Closed_Hotel: "bg-[#2FA56A]",
    Closed_Guest: "bg-[#98A0AE]",
    Declined_Hotel: "bg-[#98A0AE]",
    Declined_Guest: "bg-[#98A0AE]",
};

const formatWhen = (epochSeconds) => {
    if (!epochSeconds) return "";
    return new Date(epochSeconds * 1000).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const sumOf = (items) =>
    items.reduce((total, item) => total + (item.expected || 0), 0);

const StatusPill = ({ status }) => (
    <span
        className={`inline-block text-white text-[11px] font-bold rounded-full px-2 py-0.5 ${
            STATUS_STYLES[status] || "bg-[#98A0AE]"
        }`}
    >
        {STATUS_LABELS[status] || status}
    </span>
);

const ChargeTable = ({ items, subtotal }) => (
    <div className="overflow-x-auto">
        <table className="w-full table-fixed border-collapse min-w-[560px]">
            <colgroup>
                <col className="w-[32%]" />
                <col className="w-[18%]" />
                <col className="w-[13%]" />
                <col className="w-[12%]" />
                <col className="w-[11%]" />
                <col className="w-[14%]" />
            </colgroup>
            <thead>
                <tr>
                    <th className="text-left text-[11px] uppercase tracking-wider text-[#98A0AE] font-bold p-2 border-b border-[#E3E9F3]">
                        Request
                    </th>
                    <th className="text-left text-[11px] uppercase tracking-wider text-[#98A0AE] font-bold p-2 pl-4 border-b border-[#E3E9F3]">
                        Status
                    </th>
                    <th className="text-right text-[11px] uppercase tracking-wider text-[#98A0AE] font-bold p-2 border-b border-[#E3E9F3]">
                        Quoted
                    </th>
                    <th className="text-right text-[11px] uppercase tracking-wider text-[#98A0AE] font-bold p-2 border-b border-[#E3E9F3]">
                        Discount
                    </th>
                    <th className="text-right text-[11px] uppercase tracking-wider text-[#98A0AE] font-bold p-2 border-b border-[#E3E9F3]">
                        GST
                    </th>
                    <th className="text-right text-[11px] uppercase tracking-wider text-[#98A0AE] font-bold p-2 border-b border-[#E3E9F3]">
                        Payable
                    </th>
                </tr>
            </thead>
            <tbody>
                {items.map((item) => (
                    <tr key={item.id}>
                        <td className="p-2 border-b border-[#E3E9F3]">
                            <div className="font-semibold text-[#262B33] truncate">
                                {item.type}
                            </div>
                            <div className="text-xs text-[#98A0AE]">
                                {formatWhen(item.updated_at)}
                                {item.staff_member_name
                                    ? ` · ${item.staff_member_name}`
                                    : ""}
                            </div>
                        </td>
                        <td className="p-2 border-b border-[#E3E9F3]">
                            <StatusPill status={item.status} />
                        </td>
                        <td className="p-2 border-b border-[#E3E9F3] text-right tabular-nums">
                            {item.quoted != null ? formatINR(item.quoted) : "—"}
                        </td>
                        <td className="p-2 border-b border-[#E3E9F3] text-right tabular-nums text-[#98A0AE]">
                            {item.discount ? `− ${formatINR(item.discount)}` : "—"}
                        </td>
                        <td className="p-2 border-b border-[#E3E9F3] text-right tabular-nums text-[#98A0AE]">
                            {item.tax ? `+ ${formatINR(item.tax)}` : "—"}
                        </td>
                        <td className="p-2 border-b border-[#E3E9F3] text-right tabular-nums font-bold">
                            {item.expected != null ? (
                                formatINR(item.expected)
                            ) : (
                                <span className="text-xs font-normal text-[#98A0AE]">
                                    Not priced yet
                                </span>
                            )}
                        </td>
                    </tr>
                ))}
            </tbody>
            <tfoot>
                <tr>
                    <td
                        colSpan={5}
                        className="p-2 pt-3 border-t-[1.5px] border-[#CFD8E8] font-bold"
                    >
                        Subtotal
                    </td>
                    <td className="p-2 pt-3 border-t-[1.5px] border-[#CFD8E8] text-right tabular-nums font-bold">
                        {formatINR(subtotal)}
                    </td>
                </tr>
            </tfoot>
        </table>
    </div>
);

export default function PendingAmountsModal({ room, charges, onClose }) {
    const collectable = charges?.collectable || [];
    const open = charges?.open || [];
    const noCharge = charges?.no_charge || [];
    const total = collectable.length + open.length + noCharge.length;

    return (
        <div
            className="fixed top-0 left-0 h-screen w-screen bg-black bg-opacity-60 flex items-center justify-center z-[46]"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <div className="bg-white w-[760px] max-w-[92vw] max-h-[86vh] rounded-lg flex flex-col overflow-hidden">
                <div className="py-3 px-5 flex justify-between items-start border-b border-[#E3E9F3]">
                    <div>
                        <h2 className="text-lg font-bold text-[#262B33]">
                            Pending amounts · Room {room?.room_no}
                        </h2>
                        <p className="text-sm text-[#6B7488] mt-0.5">
                            Booking #{String(room?.booking_id || "").slice(0, 6)} ·{" "}
                            {total} {total === 1 ? "request" : "requests"}
                        </p>
                    </div>
                    <span
                        className="bg-[#EEF0FB] p-2 rounded-md cursor-pointer"
                        onClick={onClose}
                    >
                        <X className="text-[#343330]" />
                    </span>
                </div>

                <div className="flex flex-wrap gap-5 items-center px-5 py-3 bg-[#F7F9FD] border-b border-[#E3E9F3]">
                    <div className="flex flex-col pr-5 border-r border-[#E3E9F3]">
                        <span className="text-[11px] uppercase tracking-wider text-[#98A0AE] font-bold">
                            To collect
                        </span>
                        <span className="text-xl font-bold text-[#1F7A5A] tabular-nums">
                            {formatINR(charges?.to_collect)}
                        </span>
                    </div>
                    <div className="flex flex-col">
                        <span className="text-[11px] uppercase tracking-wider text-[#98A0AE] font-bold">
                            Still open
                        </span>
                        <span className="text-xl font-bold text-[#6B7488] tabular-nums">
                            {formatINR(charges?.still_open)}
                        </span>
                    </div>

                    <span className="ml-auto text-xs text-[#6B7488] bg-white border border-[#E3E9F3] rounded-full px-3 py-1">
                        Payable includes GST
                    </span>
                </div>

                <div className="overflow-y-auto px-5 pb-4">
                    {collectable.length === 0 && open.length === 0 && (
                        <p className="text-center text-[#6B7488] bg-[#F7F9FD] rounded-md p-4 my-4">
                            No chargeable requests on this room yet.
                        </p>
                    )}

                    {collectable.length > 0 && (
                        <>
                            <div className="flex items-baseline justify-between mt-4 mb-2">
                                <h3 className="text-sm font-bold text-[#262B33]">
                                    Ready to collect
                                </h3>
                                <span className="text-xs text-[#98A0AE]">
                                    Delivered and closed
                                </span>
                            </div>
                            <ChargeTable
                                items={collectable}
                                subtotal={sumOf(collectable)}
                            />
                        </>
                    )}

                    {open.length > 0 && (
                        <>
                            <div className="flex items-baseline justify-between mt-5 mb-2">
                                <h3 className="text-sm font-bold text-[#262B33]">
                                    Still open
                                </h3>
                                <span className="text-xs text-[#98A0AE]">
                                    Not delivered yet — not collectable
                                </span>
                            </div>
                            <ChargeTable items={open} subtotal={sumOf(open)} />
                        </>
                    )}

                    {noCharge.length > 0 && (
                        <>
                            <div className="flex items-baseline justify-between mt-5 mb-2">
                                <h3 className="text-sm font-bold text-[#262B33]">
                                    No charge
                                </h3>
                                <span className="text-xs text-[#98A0AE]">
                                    Free services, declines and cancellations
                                </span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {noCharge.map((item) => (
                                    <span
                                        key={item.id}
                                        className="text-xs text-[#6B7488] bg-[#F7F9FD] border border-[#E3E9F3] rounded-full px-3 py-1"
                                    >
                                        {item.type} ·{" "}
                                        {STATUS_LABELS[item.status] || item.status}
                                    </span>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
